/**
 * The en-route rows and stop dots shared by the desktop pane and the phone
 * view. Pure: no React, no i18n. Rows and their order come only from the
 * en-route summary (D1); today's Onfleet ETAs are an overlay and never add a
 * row. Every wall time is the tenant's (`flow.timezone`).
 */
import { wallTimeToUtcIso } from '@resource-kit/controllers';
import type { components } from '@/types/api.generated';
import { latestTaskEtaMs } from '../board/etaSignals';
import type { DeliveryFlowWire } from '../board/boardWire';
import { flowGateTime } from '../board/flowCopy';
import { DRIVER_COLORS } from '../hooks/useRouteMonitoringJoin';

type EnRouteSummary = components['schemas']['DispatchEnRoute'];
type RouteEtaReport = components['schemas']['DispatchRouteEtaReport'];

export interface EnRouteRow {
  key: string;
  /**
   * The Onfleet worker id, `null` when D1 has none. The phone driver list URL
   * uses it (#2677): `key` prefers the local driver id, which the ETA report
   * does not know.
   */
  workerId: string | null;
  /** `null` when neither D1 nor the ETA report names the driver. */
  driverName: string | null;
  enRoute: number;
  completed: number;
  /** Latest remaining-task ETA (unix ms); only from an ETA report of the same date. */
  etaMs: number | null;
  late: boolean;
  color: string;
}

export function buildEnRouteRows(
  summary: EnRouteSummary,
  eta: RouteEtaReport | undefined,
  windowEndMs: number | null,
): EnRouteRow[] {
  const sameDayEta = eta?.date === summary.shipping_date ? eta : undefined;
  return summary.drivers.map((driver, i) => {
    const workerId = driver.onfleet_worker_id;
    const match =
      workerId === null ? undefined : sameDayEta?.drivers.find((d) => d.workerId === workerId);
    const etaMs = match ? latestTaskEtaMs(match) : null;
    return {
      key: driver.driver_id ?? (workerId ? `worker:${workerId}` : 'unknown'),
      workerId: workerId ?? null,
      driverName: driver.driver_name ?? match?.workerName ?? null,
      enRoute: driver.en_route,
      completed: driver.completed,
      etaMs,
      late: windowEndMs !== null && etaMs !== null && etaMs > windowEndMs,
      color: DRIVER_COLORS[i % DRIVER_COLORS.length] ?? '',
    };
  });
}

export type StopDot = 'done' | 'upcoming';

/** One dot per stop of the row: the completed stops first, then the en-route ones. */
export function stopDots(row: Pick<EnRouteRow, 'completed' | 'enRoute'>): StopDot[] {
  return Array.from({ length: row.completed + row.enRoute }, (_, i) =>
    i < row.completed ? 'done' : 'upcoming',
  );
}

export interface EnRouteTimes {
  /** Tenant wall times, `HH:mm`; `null` when the flow has no such gate. */
  windowEnd: string | null;
  /** The window end of `date` as unix ms, or null without it. */
  windowEndMs: number | null;
}

/**
 * The flow times the en-route views read for one shipping day: the desktop
 * pane and the phone driver list (#2487) take them from here, so both use the
 * same window end.
 */
export function enRouteTimes(flow: DeliveryFlowWire, date: string): EnRouteTimes {
  const windowEnd = flowGateTime(flow, 'delivery_window_end');
  return {
    windowEnd,
    windowEndMs:
      windowEnd === null
        ? null
        : Date.parse(wallTimeToUtcIso(`${date}T${windowEnd}:00.000`, flow.timezone)),
  };
}
